import { Box, Paper, Skeleton, Typography } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Chip } from '@mui/material';
import { useInstitutes } from '../../hooks/useInstitutes';
import type { SystemConfigurationDto } from '../../types/institute';

export default function SystemConfigPage() {
  const { useSystemConfigs, useUpdateSystemConfig } = useInstitutes();
  const { data: sysConfigs, isLoading } = useSystemConfigs();
  const updateSysConfig = useUpdateSystemConfig();

  const columns: GridColDef<SystemConfigurationDto>[] = [
    { field: 'displayName', headerName: 'Setting', flex: 1, minWidth: 160 },
    { field: 'module', headerName: 'Module', width: 120 },
    { field: 'category', headerName: 'Category', width: 110 },
    {
      field: 'configValue',
      headerName: 'Value',
      flex: 1,
      minWidth: 140,
      editable: true,
      renderCell: ({ row }) => (
        <Typography
          variant="body1"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            color: row.isEditable ? 'text.primary' : 'text.disabled',
          }}
        >
          {row.configValue ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'isEnabled',
      headerName: 'Enabled',
      width: 90,
      renderCell: ({ row }) => (
        <Chip
          label={row.isEnabled ? 'On' : 'Off'}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.6rem',
            backgroundColor: row.isEnabled ? 'rgba(16,185,129,0.15)' : 'rgba(100,116,139,0.12)',
            color: row.isEnabled ? '#10B981' : '#64748B',
          }}
        />
      ),
    },
  ];

  return (
    <Box>
      <Paper sx={{ border: '1px solid rgba(255,255,255,0.06)' }}>
        {isLoading ? (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={44} sx={{ borderRadius: '6px' }} />
            ))}
          </Box>
        ) : (
          <DataGrid
            rows={sysConfigs ?? []}
            columns={columns}
            rowHeight={48}
            getRowId={(r) => r.configKey}
            disableRowSelectionOnClick
            processRowUpdate={(newRow) => {
              if (newRow.isEditable) {
                updateSysConfig.mutate({ key: newRow.configKey, value: newRow.configValue ?? '' });
              }
              return newRow;
            }}
            sx={{ border: 'none', minHeight: 400 }}
          />
        )}
      </Paper>
    </Box>
  );
}
